import { useQuery } from '@tanstack/react-query'
import { ofetch } from 'ofetch'
import { APP_VERSION_NUMBER } from '@/shared/config/app-version'

interface MetaJson {
  version: string
}

const UPDATE_INTERVALS = {
  development: 30 * 1000, // 30 seconds
  production: 60 * 1000, // 1 minutes
} as const

/**
 * Custom hook to detect if a new app version is available by polling /meta.json.
 * Returns true if a refresh is needed.
 *
 * This is useful for environments where service worker update events are not available.
 */
export function useAppVersionPoller() {
  const mode = (import.meta.env.MODE as keyof typeof UPDATE_INTERVALS) || 'production'

  const { data, isSuccess } = useQuery<MetaJson>({
    queryKey: ['app', 'meta'],
    queryFn: async () => {
      // Cache-busting query param to ensure a fresh response every time
      const url = `/meta.json?_cb=${Date.now()}`

      return await ofetch<MetaJson>(url, {
        cache: 'no-cache',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
        },
      })
    },
    refetchInterval: UPDATE_INTERVALS[mode],
    refetchOnWindowFocus: true,
    staleTime: 0, // always stale, always refetch
    gcTime: 0, // no cache retention
    retry: false, // do not retry on failure
  })

  if (!isSuccess || !data?.version) {
    return false
  }

  if (data.version !== APP_VERSION_NUMBER) {
    return true
  }

  return false
}
